import { useState, useEffect, useCallback } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Download, Info } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { exportBoardApi } from "@/network/network";
import { isIAmOneOfBoardOwner } from "@/other/utils/utils";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { User } from "@/bryntum-lib/models/User";
import { toast } from "sonner";
import { debounce } from "@/other/utils/otherUtils";

const GeneralBoardSettingDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user, boardManager, selectedBoard, editorHandler } = useSwivuBryntumContext();
  const [boardName, setBoardName] = useState("");
  const [boardOwner, setBoardOwner] = useState<number | null>(null);
  const [boardOwners, setBoardOwners] = useState<User[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  // Subscribe to board users store changes
  useSwivuStore({
    storeId: STORE_KEYS.boardUserStore,
    subscriberFn: () => {
      loadBoardData();
      return false; // Don't trigger re-render, we handle it manually
    }
  });

  useEffect(() => {
    loadBoardData();
  }, [selectedBoard, project, user]);

  const loadBoardData = () => {
    if (!selectedBoard || !project || !user || !boardManager) return;

    setBoardName(selectedBoard.name || "");

    const administrators = project.boardUsersStore.query(
      (boardUser: User) => boardUser.userId && boardUser.roles === GRANTS.grant_one,
      true
    ) || [];
    setBoardOwners(administrators);

    const boardStore = boardManager.boardNameStore?.getById(selectedBoard.id);
    if (boardStore) {
      // @ts-expect-error - boardStore.owner is dynamically typed
      setBoardOwner(boardStore.owner);

      // @ts-expect-error - boardStore.owner is dynamically typed
      const isOwner = isIAmOneOfBoardOwner(project, boardStore.owner);
      setCanEdit(isOwner && !user.isDemoUser && user.can(GRANTS.grant_one));
    }
  };

  // Debounced function to update the actual board record
  const debouncedUpdateBoardName = useCallback(
    debounce((value: string) => {
      if (selectedBoard && selectedBoard.record) {
        selectedBoard.record.name = value;
      }
    }, 500),
    [selectedBoard]
  );

  const handleBoardNameChange = (value: string) => {
    setBoardName(value);
    debouncedUpdateBoardName(value);
  };

  const handleBoardOwnerChange = (value: string) => {
    const ownerId = parseInt(value, 10);
    setBoardOwner(ownerId);

    if (selectedBoard && selectedBoard.record && project && boardManager && user) {
      // @ts-expect-error - selectedBoard.record is dynamically typed
      selectedBoard.record.owner = ownerId;

      const isOwner = isIAmOneOfBoardOwner(project, ownerId);
      setCanEdit(isOwner && !user.isDemoUser && user.can(GRANTS.grant_one));
    }
  };


  const handleExportBoard = async () => {
    if (!selectedBoard || !editorHandler) return;

    setIsExporting(true);
    try {
      await exportBoardApi(selectedBoard.id);
      // Show async dialog if needed
      const showDialog = shouldShowAsyncDialog('BOARD_EXPORT');
      if (showDialog && editorHandler) {
        editorHandler.openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          // @ts-expect-error - openEditor accepts null as record parameter for async message editor
          null,
          { record: "BOARD_EXPORT" },
          { useExisting: true, by: "recordType" }
        );
      }
    } catch (error) {
      toast.error("Failed to export board. Something went wrong.");
    } finally {
      setIsExporting(false);
    }
  };

  const isDisabled = user?.isDemoUser || !user?.can(GRANTS.grant_one);
  const canShareOrExport = !user?.isDemoUser && user?.can(GRANTS.grant_one);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Board Information Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Board Information</h3>
              <p className="text-xs text-muted-foreground">
                Basic board settings and ownership
              </p>
            </div>

            <div className="space-y-4">
              {/* Board Name */}
              <div className="space-y-2">
                <Label htmlFor="board-name">Board Name</Label>
                <Input
                  variant="background"
                  inputSize="selectTrigger"
                  id="board-name"
                  type="text"
                  value={boardName}
                  onChange={(e) => handleBoardNameChange(e.target.value)}
                  disabled={isDisabled}
                  placeholder="Enter board name"
                />
              </div>

              {/* Board Owner */}
              <div className="space-y-2">
                <Label htmlFor="board-owner">Board Owner</Label>
                <Select
                  value={boardOwner?.toString() || ""}
                  onValueChange={handleBoardOwnerChange}
                  disabled={!canEdit}
                >
                  <SelectTrigger id="board-owner">
                    <SelectValue placeholder="Select board owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {boardOwners.map((owner) => (
                      <SelectItem key={owner.id} value={owner.id.toString()}>
                        {owner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Owner Information */}
              <Card className="py-3 px-4 border-primary/20 bg-primary/5">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <Info className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground leading-relaxed">
                      The Board Owner cannot be deleted. However, you can designate another Administrator as the Board Owner.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Card>

        {/* Board Actions Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Board Actions</h3>
              <p className="text-xs text-muted-foreground">
                Export the entire board data
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {/* Export Board Button */}
              <Button
                onClick={handleExportBoard}
                disabled={!canShareOrExport || isExporting}
                className="flex items-center gap-2"
                variant="outline"
              >
                <Download className="h-4 w-4" />
                {isExporting ? "Exporting..." : "Export Board"}
              </Button>
            </div>

            {/* Info about Share Schedule */}
            <Card className="py-3 px-4 border-primary/20 bg-primary/5">
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Info className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground leading-relaxed">
                    Looking to share your schedule? Visit <strong>Schedule → Share Schedule</strong> in the settings menu.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GeneralBoardSettingDetail;
